// Sentence tokenizer for Reader Mode.
// Splits prose into sentence-sized speech units while keeping code blocks
// as a single announce-able token (TTS reads "code block, N lines" instead
// of every character).

export type SentenceToken =
  | { kind: 'sentence'; text: string; offset: number }
  | { kind: 'code'; lines: number; offset: number };

const ABBREV = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'st', 'mt', 'sr', 'jr',
  'e.g', 'i.e', 'etc', 'vs', 'cf', 'al',
  'fig', 'no', 'inc', 'ltd', 'co', 'corp',
]);

function isAbbrev(word: string): boolean {
  return ABBREV.has(word.toLowerCase().replace(/[.]+$/, ''));
}

/**
 * Split plain prose into sentences. Respects common abbreviations and does not
 * cut on a period that's part of "Mr.", "e.g.", a decimal number, an ellipsis,
 * or an initial like "J. R. R. Tolkien".
 *
 * Returns sentences with byte offsets relative to the original input so the
 * caller can map back into the source string.
 */
export function splitIntoSentences(text: string): { text: string; offset: number }[] {
  if (!text) return [];
  const out: { text: string; offset: number }[] = [];

  let start = 0;
  let i = 0;
  const len = text.length;

  while (i < len) {
    const c = text[i];
    if (c === '.' || c === '!' || c === '?') {
      // Skip ellipses ("...") — treat as a single terminator at the last dot.
      if (c === '.' && text[i + 1] === '.') {
        while (i < len && text[i] === '.') i++;
        // After the run of dots, if the next non-space is end-of-string or capital,
        // treat the whole run as a terminator.
        const nextNonSpace = i;
        if (nextNonSpace >= len) {
          pushSentence(out, text, start, i);
          start = i;
          continue;
        }
        // Otherwise just continue (mid-sentence ellipsis).
        continue;
      }

      // Decimal number: digit before, digit after → not a terminator.
      if (c === '.' && /\d/.test(text[i - 1] || '') && /\d/.test(text[i + 1] || '')) {
        i++;
        continue;
      }

      // Abbreviation check: backtrack to grab the preceding word.
      if (c === '.') {
        let wordStart = i;
        while (wordStart > start && /[A-Za-z]/.test(text[wordStart - 1])) wordStart--;
        const word = text.slice(wordStart, i + 1); // includes the dot
        if (word.length <= 5 && isAbbrev(word)) {
          i++;
          continue;
        }
        // Single capital letter followed by dot → initial (e.g. "J. R. R. Tolkien").
        if (word.length === 2 && /^[A-Z]\.$/.test(word)) {
          i++;
          continue;
        }
      }

      // Consume any trailing closing quotes/brackets that belong to this sentence.
      let end = i + 1;
      while (end < len && /["'\u2019\u201D)\]\}]/.test(text[end])) end++;

      // Require whitespace or end-of-input after the terminator.
      if (end >= len || /\s/.test(text[end])) {
        pushSentence(out, text, start, end);
        start = end;
        // Skip whitespace before next sentence.
        while (start < len && /\s/.test(text[start])) start++;
        i = start;
        continue;
      }
    }
    i++;
  }

  if (start < len) pushSentence(out, text, start, len);
  return out;
}

function pushSentence(
  out: { text: string; offset: number }[],
  source: string,
  from: number,
  to: number,
): void {
  const slice = source.slice(from, to).trim();
  if (slice) out.push({ text: slice, offset: from });
}

/**
 * Tokenize text containing inline code fences. Code blocks (```...```) become
 * a single 'code' token; everything else is split into sentences.
 *
 * Offsets are relative to the original input.
 */
export function tokenize(text: string): SentenceToken[] {
  if (!text) return [];

  const tokens: SentenceToken[] = [];
  const fence = /```[\s\S]*?```/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;

  while ((m = fence.exec(text)) !== null) {
    if (m.index > lastIdx) {
      const prose = text.slice(lastIdx, m.index);
      for (const s of splitIntoSentences(prose)) {
        tokens.push({ kind: 'sentence', text: s.text, offset: lastIdx + s.offset });
      }
    }
    const block = m[0];
    const lines = block.split('\n').length - 2; // exclude opening and closing fences
    tokens.push({ kind: 'code', lines: Math.max(1, lines), offset: m.index });
    lastIdx = m.index + block.length;
  }

  if (lastIdx < text.length) {
    const tail = text.slice(lastIdx);
    for (const s of splitIntoSentences(tail)) {
      tokens.push({ kind: 'sentence', text: s.text, offset: lastIdx + s.offset });
    }
  }

  return tokens;
}

/**
 * Return what TTS should actually speak for a token.
 * - sentence  → the sentence text
 * - code      → "code block, N lines" (the announce default)
 */
export function speakableText(t: SentenceToken): string {
  if (t.kind === 'sentence') return t.text;
  return `code block, ${t.lines} ${t.lines === 1 ? 'line' : 'lines'}`;
}
